import React, { useContext } from "react";
import { Button } from "@panda0/panda0-ui";
import { cn } from "@panda0/panda0-utils";
import {
  BarChart2,
  Briefcase,
  Check,
  ChevronRight,
  CircleCheck,
  Loader2,
  Settings,
  Share2,
} from "lucide-react";
import { useFormContext } from "react-hook-form";

import JobBasic from "./job-basic";
import JobCustomize from "./job-customize";
import JobInsights from "./job-insights";
import JobQuiz from "./job-quiz";
import JobShare from "./job-share";
import JobSkill from "./job-skill";
import { JobModalContext } from "./types";
import { JobStep } from "./use-job-state-machine";

function JobStepsNavigationContent() {
  const { stateMachine, savedJobId } = useContext(JobModalContext);
  const {
    currentStep,
    isSaving,
    isValidating,
    hasJobId,
    hasSkills,
    canNavigateToStep,
    saveStep,
    navigateToStep,
    nextStep,
    prevStep,
  } = stateMachine;
  const {
    formState: { errors, isDirty },
    watch,
  } = useFormContext();

  const steps: { id: JobStep; label: string; icon: any }[] = [
    { id: "basic", label: "Basic", icon: Briefcase },
    { id: "skills", label: "Skills", icon: ChevronRight },
    { id: "quiz", label: "Quiz", icon: CircleCheck },
    { id: "customize", label: "Custom", icon: Settings },
    { id: "share", label: "Share", icon: Share2 },
    { id: "insights", label: "Insight", icon: BarChart2 },
  ];

  const handleStepClick = (stepId: JobStep) => {
    if (canNavigateToStep(stepId)) {
      stateMachine.trackEvent("job_navigation_step_click", {
        from_step: currentStep,
        to_step: stepId,
        has_errors: Object.keys(errors).length > 0,
      });

      navigateToStep(stepId);
    } else {
      stateMachine.trackEvent("job_navigation_step_blocked", {
        current_step: currentStep,
        attempted_step: stepId,
        reason:
          stepId === "skills"
            ? "missing_job_id"
            : stepId === "quiz"
              ? "missing_skills"
              : "unknown",
      });
    }
  };

  const handleBack = () => {
    stateMachine.trackEvent("job_navigation_back", {
      from_step: currentStep,
      has_unsaved: isDirty,
    });

    prevStep();
  };

  const handleContinue = () => {
    stateMachine.trackEvent("job_navigation_continue", {
      from_step: currentStep,
      has_errors: Object.keys(errors).length > 0,
      validation_error_fields: Object.keys(errors),
    });

    nextStep();
  };

  const handleSave = () => {
    stateMachine.trackEvent("job_navigation_save_only", {
      step: currentStep,
      has_errors: Object.keys(errors).length > 0,
    });

    saveStep(currentStep, true);
  };

  const handleFinish = () => {
    stateMachine.trackEvent("job_builder_finish", {
      total_steps_completed: steps.length,
      skills_count: (watch("skills.technicalSkills") || []).length,
      questions_count: (watch("pandaQuiz.questions") || []).length,
      screening_questions_count: (watch("screeningQuestions") || []).length,
      candidates_count: (watch("candidates") || []).length,
      time_spent_sec:
        (Date.now() - (window as any).jobBuilderStartTime || 0) / 1000,
    });

    // Handle any final save if needed
    window.location.href = `/${savedJobId}/dashboard`;
  };

  return (
    <>
      {/* Steps and content */}
      <div className="border-b border-neutral-100 px-6 py-3 flex flex-col md:flex-row lg:flex-row">
        {/* Step indicator */}
        <div className="relative flex md:flex-col lg:flex-col justify-between md:justify-start lg:justify-start mb-2 md:mb-0 lg:mb-0">
          {steps.map((step, index) => {
            const isActive = currentStep === step.id;
            const isPrevious =
              steps.findIndex((s) => s.id === currentStep) > index;
            const isClickable = canNavigateToStep(step.id);
            const hasErrors =
              step.id === "basic" && Object.keys(errors.basic || {}).length > 0;

            return (
              <div
                key={step.id}
                className="relative z-10 flex flex-col items-center mb-0 md:mb-8 lg:mb-8"
              >
                <button
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-full border-2 transition-colors",
                    isActive
                      ? "border-neutral-900 bg-neutral-900 text-white"
                      : isPrevious
                        ? "border-neutral-700 bg-white text-neutral-700"
                        : "border-neutral-300 bg-white text-neutral-400",
                    isClickable
                      ? "cursor-pointer hover:border-neutral-500"
                      : "cursor-not-allowed opacity-60",
                    hasErrors && "border-red-500 bg-red-50 text-red-500"
                  )}
                  onClick={() => isClickable && handleStepClick(step.id)}
                  disabled={!isClickable || isSaving || isValidating}
                  title={
                    !isClickable
                      ? step.id === "skills"
                        ? "Save basic details first"
                        : step.id === "quiz"
                          ? "Define skills first"
                          : `Cannot navigate to ${step.label} yet`
                      : undefined
                  }
                >
                  {isPrevious ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    <step.icon className="h-5 w-5" />
                  )}
                </button>
                <span
                  className={cn(
                    "mt-2 text-sm font-medium",
                    isActive
                      ? "text-neutral-900"
                      : isPrevious
                        ? "text-neutral-700"
                        : "text-neutral-400",
                    hasErrors && "text-red-500"
                  )}
                >
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-auto md:ml-2">
          {currentStep === "basic" && <JobBasic />}
          {currentStep === "skills" && <JobSkill />}
          {currentStep === "quiz" && <JobQuiz />}
          {currentStep === "customize" && <JobCustomize />}
          {currentStep === "share" && <JobShare />}
          {currentStep === "insights" && <JobInsights />}
        </div>
      </div>
      {/* Footer with action buttons */}
      <div className="sticky bottom-0 z-10 flex items-center justify-between border-t border-neutral-100 bg-neutral-50 p-4">
        {currentStep !== "basic" && (
          <Button
            type="button"
            variant="secondary"
            text="Back"
            onClick={handleBack}
            disabled={isSaving || isValidating}
            className="h-9 w-fit"
          />
        )}

        <div className="ml-auto flex gap-2">
          {currentStep === "share" ? (
            <Button
              type="button"
              text="Finish"
              onClick={handleFinish}
              className="h-9 w-fit"
              disabled={!savedJobId}
              title={!savedJobId ? "Save the job first" : undefined}
            />
          ) : (
            <Button
              type="button"
              onClick={handleContinue}
              loading={isSaving}
              disabled={
                isValidating ||
                (currentStep === "basic" &&
                  Object.keys(errors.basic || {}).length > 0)
              }
              text={isSaving ? "Saving..." : "Continue"}
              className="h-9 w-fit"
            />
          )}
        </div>
      </div>
    </>
  );
}

export default React.memo(JobStepsNavigationContent);
